import { useEffect, useState } from "react";
import { RefreshCw, Save } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { saveTelephonySettings, type TelephonyStatus } from "../../../../lib/calls";
import { Btn, StatusPill } from "../../../ui";
import { FieldGroup, Note, SaveRow, ToggleRow } from "../panel/fields";

/**
 * Settings -> Telephony: ask Twilio about a number before dialling it.
 *
 * Its own file rather than more lines on the Twilio card, which is already at
 * 354 and is about credentials. This is a spending decision as much as a
 * feature one, and the two switches exist separately because the two halves of
 * Twilio Lookup are priced differently:
 *
 *   checking a number is real          free
 *   asking mobile or landline          billed per number
 *
 * So the safety half can be switched on without opening an account to a bill,
 * which is the only reason it is two toggles instead of one. The second is
 * disabled while the first is off, because it has nothing to attach to: the
 * line type arrives in the answer to a request that is only made when the gate
 * runs at all.
 */
export function LookupFields({
  status,
  onSaved,
}: {
  /** The loaded settings, or null while the card is still fetching them. */
  status: TelephonyStatus | null;
  /** Hand the PUT's answer back up, so the card and this block stay in step. */
  onSaved: (next: TelephonyStatus) => void;
}) {
  const t = useT();
  const [enabled, setEnabled] = useState(false);
  const [lineType, setLineType] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    if (!status) return;
    setEnabled(status.lookup.enabled);
    setLineType(status.lookup.lineType);
  }, [status]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      // `lineType` is sent as false whenever the gate is off, so the stored row
      // can never hold "billed field on, gate off" - a pair that would look
      // like spending and do nothing, and would start spending the moment
      // somebody flipped the gate for the safety alone.
      onSaved(await saveTelephonySettings({ lookup: { enabled, lineType: enabled && lineType } }));
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FieldGroup title={t("settings.telephony.lookup.group")}>
        <ToggleRow
          label={t("settings.telephony.lookup.enabled")}
          hint={t("settings.telephony.lookup.enabledHint")}
          checked={enabled}
          onChange={setEnabled}
        />
        <ToggleRow
          label={t("settings.telephony.lookup.lineType")}
          hint={t("settings.telephony.lookup.lineTypeHint")}
          checked={enabled && lineType}
          disabled={!enabled}
          onChange={setLineType}
        />
      </FieldGroup>

      <Note tone="info">{t("settings.telephony.lookup.note")}</Note>

      <SaveRow>
        <Btn type="submit" tone="dark" disabled={saveState === "saving" || !status}>
          {saveState === "saving" ? (
            <RefreshCw className="size-3.5 animate-spin" />
          ) : (
            <Save className="size-3.5" />
          )}
          {saveState === "saving"
            ? t("settings.telephony.lookup.saving")
            : t("settings.telephony.lookup.save")}
        </Btn>
        {saveState === "saved" ? (
          <StatusPill tone="ok">{t("settings.telephony.lookup.saved")}</StatusPill>
        ) : null}
        {saveState === "error" ? (
          <StatusPill tone="lt">{t("settings.telephony.lookup.saveFailed")}</StatusPill>
        ) : null}
      </SaveRow>
    </form>
  );
}
